import { useTranslation } from 'react-i18next';
import { Li, Link, P, Ul } from '@vp/swan';
import { sanitiseLocale } from '../../../../i18n/config';
import { ninetyNineLogoDesignUrl, proLogoDesignUrl } from '../../../../utils/urls';
import type { FAQ } from '../../../../components/FAQAccordion/FAQAccordion';

export const useFaqData = (): FAQ[] => {
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    return [
        {
            id: 'whats-the-difference',
            question:
                'What makes AI Logomaker by VistaPrint® different from other AI logo generators?',
            answer: (
                <>
                    <P>
                        Unlike most free AI logo generators that rely on generic templates or stock
                        icons, VistaPrint's AI logo maker is purpose-built for logo creation. Our
                        generative AI has been trained on design patterns from millions of real
                        small business logos to deliver designs that truly fit your industry, style
                        and goals. You'll also get:
                    </P>
                    <Ul>
                        <Li>
                            <strong>More freedom to explore for free –</strong> with one of the most
                            generous free credit offerings in the market, our AI logo maker lets you
                            refine and customize until it feels just right. There are also no hidden
                            costs with AI Logomaker by VistaPrint® – you will receive all of your
                            logo files (including transparent vector file downloads) for free.
                        </Li>
                        <Li>
                            <strong>Powerful editing tools –</strong> to personalize your design and
                            iterate simply by explaining what changes you'd like made, without
                            starting over.
                        </Li>
                        <Li>
                            <strong>Apply and print it everywhere, instantly –</strong> instantly
                            download free SVG, PDF and transparent PNG files (crisp and clear at any
                            size!) ready to use anywhere across VistaPrint's full range of products
                            or anywhere else.
                        </Li>
                        <Li>
                            <strong>Human design support –</strong> if you want a professional to
                            touch up your AI logo design, our talented, vetted designers are
                            available at a fraction of the cost of traditional design services.
                        </Li>
                        <Li>
                            <strong>Built-in Brand Kit integration –</strong> your final AI logo
                            files connect seamlessly to your VistaPrint Brand Kit helping you build
                            a consistent look across business cards, social posts and more.{' '}
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'is-the-logo-design-free',
            question: 'Is the logo design free?',
            answer: (
                <>
                    <P>
                        Yes! AI Logomaker by VistaPrint® is 100% free to create, customize and
                        download your logo. After creating your AI logo design, you'll get
                        high-resolution file formats and a Brand Kit that you can use across your
                        VistaPrint projects – like social media posts, websites and printed
                        products.
                    </P>
                    <P>
                        You'll get three colored background logo files in PNG, SVG and PDF formats,
                        and three transparent background logo files in PNG, SVG and PDF formats –
                        all for free.
                    </P>
                </>
            ),
        },
        {
            id: 'how-do-i-customize-ai-generated-logo',
            question: 'How do I customize my AI-generated logo for my brand?',
            answer: (
                <>
                    <P>
                        Once your logo is generated, you're in full control. Our AI Logomaker gives
                        you powerful editing tools to refine and personalize every detail – from
                        icons and layouts to fonts and colors – so your design feels just right.
                    </P>
                    <P>You can:</P>
                    <Ul>
                        <Li>
                            <strong>Prompt better –</strong> our simple, guided flow helps you
                            describe your logo clearly so the AI logo generator instantly
                            understands your style and goals.
                        </Li>
                        <Li>
                            <strong>Personalize without limits –</strong> refine your AI logo with
                            ease. Move between iterations instead of starting from scratch and tweak
                            icons, layouts, fonts, or colors. Even prompt simple changes like "make
                            the font bolder" or "change the color palette to blue" – all while
                            keeping the original concept intact.
                        </Li>
                        <Li>
                            <strong>Get a human designer's touch –</strong> bring in a professional
                            VistaPrint designer anytime for expert refinements that elevate your
                            logo from great to next-level amazing!
                        </Li>
                    </Ul>
                </>
            ),
        },
        {
            id: 'can-i-add-my-tagline-to-my-logo-design',
            question: 'Can I add my tagline to my logo design?',
            answer: (
                <>
                    <P>
                        It's easy to edit the text in your logo, whether it's your business name,
                        tagline, subtitle or something else. Add, remove or change your logo text at
                        any time during AI logo creation.
                    </P>
                </>
            ),
        },
        {
            id: 'are-logos-unique-and-copyright-safe',
            question: 'Are logos created with free logo makers unique and copyright safe?',
            answer: (
                <>
                    <P>That depends on how you design your logo.</P>
                    <P>
                        If you use our free generative AI Logomaker, it will design entire logos
                        without templates or stock. This makes your AI logo design unique to your
                        prompts and is safe for business or personal use – however, AI-generated
                        content is not considered to be the work of a human creator, meaning it
                        fails to meet the legal requirements for copyright protection in many
                        jurisdictions.
                    </P>
                    <P>
                        If this is important to you, paying a professional designer to{' '}
                        <Link href={proLogoDesignUrl(locale)}>
                            create a handcrafted logo from scratch
                        </Link>{' '}
                        might be your best choice. Whether you hire a designer or start a{' '}
                        <Link href={ninetyNineLogoDesignUrl(locale)}>
                            design contest via 99designs
                        </Link>
                        , the finished result will be a completely custom logo that can be
                        copyrighted or trademarked.
                    </P>
                </>
            ),
        },
        {
            id: 'can-i-edit-my-logo',
            question: 'Can I edit my AI-generated logo after downloading it?',
            answer: (
                <>
                    <P>
                        Absolutely! AI Logomaker by VistaPrint® is one of the only free logomakers
                        that gives you access to editable files for free. After you download your
                        logo files, you can modify them – whether you do it yourself with design
                        software or have one of our professional designers make updates for you.
                    </P>
                    <P>
                        You can always revisit your logo design project at any time via the "My
                        Projects" tab in your VistaPrint account, and tweak it to better align to
                        your brand.
                    </P>
                </>
            ),
        },
        {
            id: 'what-logo-files-do-i-get',
            question: 'What logo files do I get? Are they professional quality?',
            answer: (
                <>
                    <P>
                        You'll get one full-color logo and one transparent background logo in the
                        following file types, all for free:
                    </P>
                    <Ul>
                        <Li>SVG - A vector graphic that can be resized without losing quality</Li>
                        <Li>
                            PNG - A raster graphic that's great for websites and social media posts
                        </Li>
                        <Li>
                            PDF - A printable format that can be viewed without proprietary software
                        </Li>
                    </Ul>
                    <P>
                        These are 4000x4000 pixel, high-resolution image files without watermarks,
                        so you can use your AI logo on all personal or professional marketing
                        materials.
                    </P>
                    <P>
                        Other free AI logomakers typically deliver low-resolution logo images that
                        can appear fuzzy or pixelated on bigger projects. But with a VistaPrint AI
                        logo design, your logo will look sharp and professional anyway you use it!
                    </P>
                </>
            ),
        },
        {
            id: 'can-i-ask-for-professional-designer-support',
            question:
                "Can I ask for professional designers' support while using or after using AI Logomaker by VistaPrint®?",
            answer: (
                <>
                    <P>Of course! We're here to help you get the perfect logo.</P>
                    <P>
                        During the process of creating your logo using our generative AI logomaker
                        (or after you download it), you can easily reach out to a professional
                        designer to add any details or finishing touches for a small fee.{' '}
                    </P>
                    <P>
                        When you finish downloading your logo, our Customer Care specialists can
                        also review your logo and help with any technical, product or design
                        questions you may have.
                    </P>
                </>
            ),
        },
        {
            id: 'can-i-upload-my-own-designs',
            question: 'Can I upload my own designs and pictures to AI Logomaker by VistaPrint®?',
            answer: (
                <>
                    <P>Right now, you can't upload your own designs or pictures.</P>
                </>
            ),
        },
    ];
};
